import { createContext, useContext, useEffect, useState } from "react";
import { AdminUser } from "@/types";
import { apiClient } from "@/services/api/client";
import { normalizeEthiopianSubscriberNumber } from "@/lib/phone";

type AuthContextType = {
  user: AdminUser | null;
  isAuthenticated: boolean;
  initialized: boolean;
  login: (
    phoneNumber: string,
    password: string,
  ) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  updateUser: (updatedUser: Partial<AdminUser>) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function profileFromResponse(payload: unknown): Record<string, unknown> | null {
  const envelope = asRecord(payload);
  const response = asRecord(envelope?.data) ?? envelope;
  return asRecord(response?.profile) ?? asRecord(response?.user) ?? response;
}

function toAdminUser(profile: unknown): AdminUser | null {
  const record = asRecord(profile);
  if (!record || !(record.id || record.name || record.email)) return null;

  const role = asRecord(record.currentRole)?.name ?? asRecord(record.role)?.name ?? record.role;
  const roleName = typeof role === "string" ? role : "Super Admin";

  return {
    id: typeof record.id === "string" ? record.id : "admin",
    name: (typeof record.name === "string" ? record.name : "Scorenova Admin").replace(/Learnova/gi, "Scorenova"),
    email: (typeof record.email === "string" ? record.email : "admin@scorenova.et").replace(/learnova/gi, "scorenova"),
    role: roleName as AdminUser["role"],
    lastLogin: new Date().toISOString(),
  };
}

function clearLegacyCredentials() {
  ["token", "accessToken", "refreshToken", "user"].forEach((key) =>
    localStorage.removeItem(key),
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    clearLegacyCredentials();

    apiClient
      .get<unknown>("/auth/get-user-info", undefined, { timeoutMs: 10000, retries: 0 })
      .then((payload) => setUser(toAdminUser(profileFromResponse(payload))))
      .catch(() => setUser(null))
      .finally(() => setInitialized(true));

    const handleLogout = () => setUser(null);
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, []);

  const login = async (phoneNumber: string, password: string) => {
    try {
      const subscriberNumber = normalizeEthiopianSubscriberNumber(phoneNumber);
      if (subscriberNumber.length !== 9) {
        return {
          success: false,
          message: "Enter a valid phone number with 9 digits, or include the 251 country code.",
        };
      }
      const payload = await apiClient.post<unknown>("/auth/login", {
        phoneNumber: subscriberNumber,
        password,
        loginAs: "admin",
        fcmId: "web-admin",
      });

      const loginResult = asRecord(payload)?.data ?? payload;
      const loginRecord = asRecord(loginResult);
      if (loginRecord?.accessToken || loginRecord?.refreshToken) {
        clearLegacyCredentials();
        return {
          success: false,
          message: "Secure admin sign-in is not enabled by the server yet.",
        };
      }

      const loggedInUser = toAdminUser(profileFromResponse(payload));
      if (!loggedInUser) {
        return {
          success: false,
          message: "The server did not return an admin profile. Please try again.",
        };
      }

      setUser(loggedInUser);
      setInitialized(true);
      window.dispatchEvent(new Event("auth:login"));
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred. Please try again.";
      if (error instanceof DOMException && error.name === "AbortError" || message.includes("aborted")) {
        return { success: true };
      }
      return {
        success: false,
        message,
      };
    }
  };

  const logout = async () => {
    setUser(null);
    clearLegacyCredentials();
    try {
      await apiClient.post("/auth/logout", {});
    } catch {
      // Clear the local UI state even when the server cannot be reached.
    } finally {
      window.dispatchEvent(new Event("auth:logout"));
    }
  };

  const updateUser = (updatedUser: Partial<AdminUser>) => {
    setUser((previous) => previous ? { ...previous, ...updatedUser } : null);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, initialized, login, logout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};
